import { YoutubePlayerProps } from '@livekatsomo/types';
import Box from '@mui/material/Box';
import { useEffect, useRef, useState } from 'react';
import { BaseReactPlayerProps } from 'react-player/base';
import ReactPlayer from 'react-player/youtube';

// Render a YouTube video player
export function YoutubePlayer({
  onSetPlayerControls,
  muted = false,
  autoplay,
  source,
}: Omit<YoutubePlayerProps, 'type'>) {
  const playerRef = useRef<ReactPlayer>(null);
  const [state, setState] = useState<BaseReactPlayerProps>({
    playing: autoplay,
    muted,
  });

  useEffect(() => {
    if (onSetPlayerControls) {
      onSetPlayerControls({
        play: () => setState((prev) => ({ ...prev, playing: true })),
        pause: () => setState((prev) => ({ ...prev, playing: false })),
        seek: (seconds: number) =>
          Promise.resolve(playerRef.current?.seekTo(seconds, 'seconds')),
        getCurrentTime: () =>
          Promise.resolve(playerRef.current?.getCurrentTime() || 0),
        getDuration: () =>
          Promise.resolve(playerRef.current?.getDuration() || 0),
        isLive: () => false,
        isPlaying: () => Promise.resolve(state.playing || false),
        togglePlay: () =>
          setState((prev) => ({ ...prev, playing: !prev.playing })),
        mute: () => setState((prev) => ({ ...prev, muted: true })),
        toggleMute: () => setState((prev) => ({ ...prev, muted: !prev.muted })),
        unmute: () => setState((prev) => ({ ...prev, muted: false })),
        on: (event, callback) => {
          switch (event) {
            case 'playback':
              setState((prev) => ({
                ...prev,
                onPlay: callback,
              }));
              break;
            case 'playbackEnded':
              setState((prev) => ({
                ...prev,
                onEnded: callback,
              }));
              break;
            case 'sourceLoaded':
              setState((prev) => ({
                ...prev,
                onReady: callback,
              }));
              break;
            case 'timeupdate':
              setState((prev) => ({
                ...prev,
                onProgress: (state) => callback(state.played),
              }));
              break;
          }
        },
        off: (event, callback) => {
          switch (event) {
            case 'playback':
              setState((prev) => ({
                ...prev,
                onPlay: undefined,
              }));
              break;
            case 'playbackEnded':
              setState((prev) => ({
                ...prev,
                onEnded: undefined,
              }));
              break;
            case 'sourceLoaded':
              setState((prev) => ({
                ...prev,
                onReady: undefined,
              }));
              break;
            case 'timeupdate':
              setState((prev) => ({
                ...prev,
                onProgress: undefined,
              }));
              break;
          }
        },
      });
    }
  }, [onSetPlayerControls, state.playing]);

  return (
    <Box
      width="100%"
      height="100%"
      sx={{
        aspectRatio: '16 / 9',
      }}
    >
      <ReactPlayer
        ref={playerRef}
        {...state}
        url={source}
        controls
        width={'100%'}
        height={'100%'}
      />
    </Box>
  );
}

export default YoutubePlayer;
